import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { systemClock, type BusinessDate } from '@sakhtar/calendar';
import {
  Icon,
  LoadingState,
  StatePanel,
  V2Alert,
  V2Badge,
  V2Button,
  V2Card,
  V2CardBody,
  V2CardHeader,
  V2ConfirmDialog,
  V2DataGrid,
  V2EmptyState,
  V2FormField,
  V2MetaStrip,
  V2Modal,
  V2ToastStack,
  type V2GridColumn,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { amountColumnHeader, formatRialFa } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type {
  CountSessionDetailResponse,
  CountSessionItem,
  CountSessionLineItem,
} from '../../lib/types/inventory.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import {
  COUNT_SESSION_STATUS_META,
  formatQuantity,
  isTruncated,
  metaOf,
  normalizeDigits,
  pickerOptions,
  stateVariantFor,
} from './inventoryDisplay.js';
import { useToasts } from './useInventoryUi.js';
import './inventory.css';

const PICKER_LIMIT = 200;

/** What the count box starts with: the saved count, else the system quantity — whole numbers only. */
function initialCountText(line: CountSessionLineItem): string {
  const q = line.countedQty ?? line.systemQty;
  return q.denominator === '1' ? q.numerator : '';
}

const digitsOnly = (value: string): string => normalizeDigits(value).replace(/\D/g, '');

export function CountSessionsPage() {
  const { slug, sessionId } = useParams<{ slug: string; sessionId?: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const navigate = useNavigate();
  const { unit, label: amountLabel } = useAmountDisplay();
  const { toasts, push, dismiss } = useToasts();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [sessionDate, setSessionDate] = useState(systemClock.today());
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // List of sessions
  const sessionsResult = useApiGet<{ sessions: readonly CountSessionItem[] }>(
    slug && !sessionId ? `${base}/api/v1/inventory/count-sessions` : null,
    [slug, sessionId],
  );

  // Detail of a session
  const sessionDetail = useApiGet<CountSessionDetailResponse>(
    slug && sessionId ? `${base}/api/v1/inventory/count-sessions/${sessionId}` : null,
    [slug, sessionId],
  );

  const warehouses = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/warehouses?limit=${PICKER_LIMIT}` : null,
    [slug],
  );
  const warehouseOptions = useMemo(
    () =>
      warehouses.status === 'ready' ? pickerOptions(warehouses.data.items, 'warehouseId') : [],
    [warehouses],
  );

  // Draft counts for editing
  const [draftCounts, setDraftCounts] = useState<Record<string, string>>({});
  const [draftNotes, setDraftNotes] = useState<Record<string, string>>({});
  const [savingCounts, setSavingCounts] = useState(false);
  const [postingAdjustments, setPostingAdjustments] = useState(false);
  const [confirmPostOpen, setConfirmPostOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Barcode / Serial fast scan state
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scanFeedback, setScanFeedback] = useState<{
    message: string;
    tone: 'success' | 'warn';
  } | null>(null);
  const [highlightedLineId, setHighlightedLineId] = useState<string | null>(null);

  // Initialize draft counts whenever a (re)loaded session brings its lines
  const fetchedLines = sessionDetail.status === 'ready' ? sessionDetail.data.lines : null;
  useEffect(() => {
    if (!fetchedLines?.length) return;
    const counts: Record<string, string> = {};
    const notes: Record<string, string> = {};
    for (const l of fetchedLines) {
      counts[l.countLineId] = initialCountText(l);
      notes[l.countLineId] = l.notes || '';
    }
    setDraftCounts(counts);
    setDraftNotes(notes);
  }, [fetchedLines]);
  const lines = fetchedLines ?? [];

  async function handleCreateSession() {
    if (!selectedWarehouseId) {
      setCreateError('لطفاً انبار را انتخاب کنید.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const result = await apiPost<CountSessionItem>(`${base}/api/v1/inventory/count-sessions`, {
        warehouseId: selectedWarehouseId,
        sessionDate,
        description,
      });
      setShowCreateModal(false);
      navigate(`${base}/inventory/count-sessions/${result.sessionId}`);
    } catch (err: unknown) {
      setCreateError(bannerMessageFrom(err, 'ایجاد سشن انبارگردانی انجام نشد.'));
    } finally {
      setCreating(false);
    }
  }

  async function handleSaveCounts() {
    if (!sessionId || !lines.length) return;
    setSavingCounts(true);
    setActionError(null);
    try {
      const payload = lines.map((l) => ({
        countLineId: l.countLineId,
        countedQuantity: {
          numerator: draftCounts[l.countLineId] || '0',
          denominator: '1',
        },
        notes: draftNotes[l.countLineId] || '',
      }));
      await apiPost(`${base}/api/v1/inventory/count-sessions/${sessionId}/counts`, {
        lines: payload,
      });
      sessionDetail.reload?.();
      push('شمارش فیزیکی ذخیره و مغایرت‌ها محاسبه شد.');
    } catch (err: unknown) {
      setActionError(bannerMessageFrom(err, 'ذخیرهٔ شمارش انجام نشد.'));
    } finally {
      setSavingCounts(false);
    }
  }

  async function handlePostAdjustments() {
    if (!sessionId) return;
    setPostingAdjustments(true);
    setActionError(null);
    try {
      const sessionDate =
        sessionDetail.status === 'ready' ? sessionDetail.data.session.sessionDate : undefined;
      const result = await apiPost<{ movementId: string; postedCount: number }>(
        `${base}/api/v1/inventory/count-sessions/${sessionId}/post`,
        { businessDate: sessionDate },
      );
      setConfirmPostOpen(false);
      sessionDetail.reload?.();
      push(
        `ثبت نهایی انجام شد. ${result.postedCount.toLocaleString('fa-IR')} ردیف تعدیل در کاردکس و دفاتر انبار ثبت شد.`,
      );
    } catch (err: unknown) {
      setConfirmPostOpen(false);
      setActionError(bannerMessageFrom(err, 'ثبت نهایی انجام نشد.'));
    } finally {
      setPostingAdjustments(false);
    }
  }

  function handleBarcodeScan(e: FormEvent) {
    e.preventDefault();
    const query = barcodeInput.trim().toLowerCase();
    if (!query) return;

    const matched = lines.find(
      (l) =>
        l.productCode.toLowerCase() === query ||
        l.serialNumber?.toLowerCase() === query ||
        l.lotCode?.toLowerCase() === query ||
        l.locationCode.toLowerCase() === query,
    );

    if (matched) {
      const currentVal = BigInt(draftCounts[matched.countLineId] || '0');
      const newVal = (currentVal + 1n).toString();
      setDraftCounts((prev) => ({ ...prev, [matched.countLineId]: newVal }));
      setHighlightedLineId(matched.countLineId);
      setScanFeedback({
        message: `کالای «${matched.productName}» (${matched.productCode}) اسکن شد: شمارش به ${BigInt(newVal).toLocaleString('fa-IR')} افزایش یافت.`,
        tone: 'success',
      });
      setBarcodeInput('');
    } else {
      setScanFeedback({
        message: `موردی با بارکد یا شناسه «${barcodeInput}» در این جلسه انبارگردانی یافت نشد.`,
        tone: 'warn',
      });
    }
  }

  // If viewing a single session detail
  if (sessionId) {
    if (sessionDetail.status === 'loading') {
      return <LoadingState label="در حال بارگذاری جزئیات سشن انبارگردانی…" />;
    }
    if (sessionDetail.status === 'error') {
      return (
        <StatePanel
          variant={stateVariantFor(sessionDetail.error.status)}
          title={
            sessionDetail.error.status === 403
              ? 'دسترسی مشاهدهٔ این سشن را ندارید'
              : 'سشن انبارگردانی یافت نشد'
          }
          action={
            <Link
              to={`${base}/inventory/count-sessions`}
              className="sk-btn sk-btn--secondary sk-btn--md inv-btn-link"
            >
              بازگشت به فهرست سشن‌ها
            </Link>
          }
        />
      );
    }

    const { session, lines: sessionLines } = sessionDetail.data;
    const isPosted = session.status === 'posted';
    const statusMeta = metaOf(COUNT_SESSION_STATUS_META, session.status);

    const columns: V2GridColumn<CountSessionLineItem>[] = [
      {
        key: 'product',
        header: 'کالا',
        width: 200,
        wrap: true,
        hideable: false,
        render: (line) => (
          <span className="inv-cell">
            <span className="inv-cell__main">{line.productName}</span>
            <span className="inv-cell__sub ltr">{line.productCode}</span>
          </span>
        ),
      },
      {
        key: 'system',
        header: 'موجودی سیستم',
        align: 'end',
        render: (line) => <span className="sk-num">{formatQuantity(line.systemQty)}</span>,
      },
      {
        key: 'counted',
        header: 'شمارش فیزیکی',
        align: 'end',
        emphasis: 'key',
        render: (line) =>
          !isPosted ? (
            <input
              className="sk-input inv-count-input ltr sk-num"
              inputMode="numeric"
              aria-label={`شمارش ${line.productName}`}
              value={draftCounts[line.countLineId] ?? ''}
              onChange={(e) =>
                setDraftCounts((prev) => ({
                  ...prev,
                  [line.countLineId]: digitsOnly(e.target.value),
                }))
              }
            />
          ) : (
            <span className="sk-num">{formatQuantity(line.countedQty)}</span>
          ),
      },
      {
        key: 'variance',
        header: 'مغایرت مقداری',
        render: (line) => {
          const n = BigInt(line.varianceQty.numerator);
          return n === 0n ? (
            <V2Badge tone="neutral">منطبق</V2Badge>
          ) : n < 0n ? (
            <V2Badge tone="danger">کسری: {formatQuantity(line.varianceQty)}</V2Badge>
          ) : (
            <V2Badge tone="success">اضافی: +{formatQuantity(line.varianceQty)}</V2Badge>
          );
        },
      },
      {
        key: 'location',
        header: 'محل انبار',
        muted: true,
        render: (line) => `${line.locationCode} — ${line.locationName}`,
      },
      {
        key: 'tracking',
        header: 'بهر / سریال',
        muted: true,
        render: (line) => <span className="ltr">{line.lotCode || line.serialNumber || '—'}</span>,
      },
      {
        key: 'unitCost',
        header: amountColumnHeader('فی میانگین', unit),
        align: 'end',
        render: (line) => (
          <span className="sk-num">
            {line.unitCostRial ? formatRialFa(line.unitCostRial, unit) : '—'}
          </span>
        ),
      },
      {
        key: 'varianceValue',
        header: amountColumnHeader('ارزش مغایرت', unit),
        align: 'end',
        render: (line) => {
          const n = BigInt(line.varianceQty.numerator);
          return (
            <span className={`sk-num ${n < 0n ? 'inv-tone-down' : n > 0n ? 'inv-tone-up' : ''}`}>
              {formatRialFa(line.varianceValueRial ?? '0', unit)}
            </span>
          );
        },
      },
      {
        key: 'notes',
        header: 'یادداشت انباردار',
        render: (line) =>
          !isPosted ? (
            <input
              className="sk-input inv-note-input"
              placeholder="علت مغایرت…"
              aria-label={`یادداشت ${line.productName}`}
              value={draftNotes[line.countLineId] ?? ''}
              onChange={(e) =>
                setDraftNotes((prev) => ({ ...prev, [line.countLineId]: e.target.value }))
              }
            />
          ) : (
            <span>{line.notes || '—'}</span>
          ),
      },
    ];

    return (
      <div className="inv-page">
        <V2Card>
          <V2CardHeader
            icon="check"
            title={`سشن انبارگردانی ${session.sessionNumber}`}
            actions={<V2Badge tone={statusMeta.tone}>{statusMeta.label}</V2Badge>}
          />
          <V2MetaStrip
            items={[
              { label: 'انبار', value: `${session.warehouseCode} — ${session.warehouseName}` },
              {
                label: 'تاریخ رسمی انبارگردانی',
                value: <span className="sk-num">{displayJalaliDate(session.sessionDate)}</span>,
              },
              {
                label: 'تعداد اقلام شمارش',
                value: <span className="sk-num">{session.totalLines.toLocaleString('fa-IR')}</span>,
              },
              {
                label: 'تعداد دارای مغایرت',
                value: (
                  <V2Badge tone={session.varianceLines > 0 ? 'warn' : 'success'}>
                    {session.varianceLines.toLocaleString('fa-IR')}
                  </V2Badge>
                ),
              },
            ]}
          />
          <V2CardBody>
            <div className="inv-form">
              {session.description ? (
                <p className="inv-form__lead">توضیحات: {session.description}</p>
              ) : null}

              {actionError ? (
                <V2Alert tone="danger" onClose={() => setActionError(null)}>
                  {actionError}
                </V2Alert>
              ) : null}

              <div className="inv-actions">
                {!isPosted ? (
                  <V2Button variant="primary" onClick={handleSaveCounts} disabled={savingCounts}>
                    {savingCounts ? 'در حال ذخیره و محاسبه…' : 'ذخیرهٔ شمارش و محاسبهٔ مغایرت'}
                  </V2Button>
                ) : null}
                {!isPosted && session.status === 'reconciled' ? (
                  <V2Button
                    variant="secondary"
                    onClick={() => setConfirmPostOpen(true)}
                    disabled={postingAdjustments}
                  >
                    تأیید نهایی و صدور اسناد تعدیل کسری/اضافی
                  </V2Button>
                ) : null}
                <Link
                  to={`${base}/inventory/count-sessions/${sessionId}/print`}
                  target="_blank"
                  rel="noreferrer"
                  className="sk-btn sk-btn--secondary sk-btn--md inv-btn-link"
                >
                  <Icon name="invoice" size="sm" />
                  <span>چاپ فرم رسمی شمارش</span>
                </Link>
                <Link
                  to={`${base}/inventory/count-sessions`}
                  className="sk-btn sk-btn--ghost sk-btn--md inv-btn-link"
                >
                  بازگشت به فهرست
                </Link>
              </div>

              {isPosted ? (
                <V2Alert tone="success">
                  این سشن با موفقیت ثبت نهایی شده و اسناد تعدیل صادر گردیده است.
                </V2Alert>
              ) : null}
            </div>
          </V2CardBody>
        </V2Card>

        {!isPosted ? (
          <V2Card>
            <V2CardBody>
              <div className="inv-form">
                <form className="inv-scan" onSubmit={handleBarcodeScan}>
                  <input
                    type="text"
                    className="sk-input ltr"
                    aria-label="اسکن بارکد"
                    value={barcodeInput}
                    onChange={(e) => setBarcodeInput(e.target.value)}
                    placeholder="اسکن سریع بارکد کالا، شمارهٔ سریال یا کد بهر (برای ثبت Enter بزنید)"
                  />
                  <V2Button variant="primary" type="submit">
                    ثبت اسکن
                  </V2Button>
                </form>
                {scanFeedback ? (
                  <V2Alert tone={scanFeedback.tone}>{scanFeedback.message}</V2Alert>
                ) : null}
              </div>
            </V2CardBody>
          </V2Card>
        ) : null}

        <V2DataGrid
          icon="tasks"
          title="برگهٔ شمارش و مقایسهٔ کسری / اضافی"
          subtitle={`${sessionLines.length.toLocaleString('fa-IR')} قلم کالایی`}
          columns={columns}
          rows={sessionLines}
          rowKey={(l) => l.countLineId}
          selectedKey={highlightedLineId}
          rowTone={(l) => {
            const n = BigInt(l.varianceQty.numerator);
            return n < 0n ? 'danger' : n > 0n ? 'success' : undefined;
          }}
          pageSize={0}
          densitySwitch={false}
          maxHeight={640}
          emptyState={
            <V2EmptyState
              icon="inventory"
              title="این سشن قلمی ندارد"
              description="در انبار انتخاب‌شده هنوز موجودی‌ای برای شمارش ثبت نشده است."
              compact
            />
          }
        />

        <V2ConfirmDialog
          open={confirmPostOpen}
          danger
          title="ثبت نهایی سشن انبارگردانی؟"
          message="با ثبت نهایی، اسناد تعدیل کسری و اضافی صادر و در کاردکس و دفاتر انبار ثبت می‌شود و قابل بازگشت نیست. ادامه می‌دهید؟"
          confirmLabel="ثبت نهایی و صدور اسناد"
          loading={postingAdjustments}
          onConfirm={() => void handlePostAdjustments()}
          onCancel={() => setConfirmPostOpen(false)}
        />

        <V2ToastStack toasts={toasts} onDismiss={dismiss} />
      </div>
    );
  }

  // List View of All Count Sessions
  const sessionColumns: V2GridColumn<CountSessionItem>[] = [
    {
      key: 'number',
      header: 'شمارهٔ سشن',
      hideable: false,
      render: (s) => (
        <Link
          to={`${base}/inventory/count-sessions/${s.sessionId}`}
          className="inv-code ltr inv-link"
        >
          {s.sessionNumber}
        </Link>
      ),
    },
    {
      key: 'warehouse',
      header: 'انبار',
      render: (s) => `${s.warehouseCode} — ${s.warehouseName}`,
    },
    {
      key: 'date',
      header: 'تاریخ شمارش',
      render: (s) => <span className="sk-num">{displayJalaliDate(s.sessionDate)}</span>,
    },
    {
      key: 'status',
      header: 'وضعیت',
      render: (s) => {
        const meta = metaOf(COUNT_SESSION_STATUS_META, s.status);
        return <V2Badge tone={meta.tone}>{meta.label}</V2Badge>;
      },
    },
    {
      key: 'total',
      header: 'اقلام کل',
      align: 'end',
      render: (s) => <span className="sk-num">{s.totalLines.toLocaleString('fa-IR')}</span>,
    },
    {
      key: 'variance',
      header: 'اقلام مغایرت‌دار',
      align: 'end',
      render: (s) => (
        <span className="sk-num">
          {s.varianceLines > 0 ? (
            <V2Badge tone="warn">{s.varianceLines.toLocaleString('fa-IR')}</V2Badge>
          ) : (
            s.varianceLines.toLocaleString('fa-IR')
          )}
        </span>
      ),
    },
    {
      key: 'value',
      header: `ارزش کل مغایرت (${amountLabel})`,
      align: 'end',
      render: (s) => <span className="sk-num">{formatRialFa(s.totalVarianceValueRial, unit)}</span>,
    },
    {
      key: 'actions',
      header: 'عملیات',
      hideable: false,
      render: (s) => (
        <Link
          to={`${base}/inventory/count-sessions/${s.sessionId}`}
          className="sk-btn sk-btn--secondary sk-btn--sm inv-btn-link"
        >
          مشاهده و شمارش
        </Link>
      ),
    },
  ];

  return (
    <div className="inv-page">
      {sessionsResult.status === 'loading' ? (
        <LoadingState label="در حال دریافت سشن‌های انبارگردانی…" />
      ) : null}
      {sessionsResult.status === 'error' ? (
        <StatePanel
          variant={stateVariantFor(sessionsResult.error.status)}
          title={
            sessionsResult.error.status === 403
              ? 'دسترسی مشاهدهٔ انبارگردانی را ندارید'
              : 'فهرست سشن‌های انبارگردانی قابل نمایش نیست'
          }
        />
      ) : null}

      {sessionsResult.status === 'ready' ? (
        <V2DataGrid
          icon="tasks"
          title="سشن‌های انبارگردانی رسمی"
          subtitle={`${sessionsResult.data.sessions.length.toLocaleString('fa-IR')} سشن`}
          actions={
            <V2Button variant="primary" icon="plus" onClick={() => setShowCreateModal(true)}>
              سشن انبارگردانی جدید
            </V2Button>
          }
          columns={sessionColumns}
          rows={sessionsResult.data.sessions}
          rowKey={(s) => s.sessionId}
          rowTone={(s) => (s.varianceLines > 0 ? 'warn' : undefined)}
          zebra
          storageKey="inventory.count-sessions"
          emptyState={
            <V2EmptyState
              icon="tasks"
              title="هیچ سشن انبارگردانی ثبت نشده است"
              description="برای شروع شمارش فیزیکی رسمی، مقایسهٔ کسری/اضافی و صدور برگهٔ شمارش، سشن جدید ایجاد کنید."
              action={
                <V2Button variant="primary" onClick={() => setShowCreateModal(true)}>
                  ایجاد اولین سشن انبارگردانی
                </V2Button>
              }
            />
          }
        />
      ) : null}

      <V2Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        dismissible={!creating}
        size="md"
        title="ایجاد سشن انبارگردانی جدید"
        footer={
          <>
            <V2Button
              type="button"
              variant="ghost"
              onClick={() => setShowCreateModal(false)}
              disabled={creating}
            >
              انصراف
            </V2Button>
            <V2Button
              type="submit"
              form="count-session-create-form"
              variant="primary"
              disabled={creating}
            >
              {creating ? 'در حال ایجاد سشن…' : 'ایجاد سشن و ثبت تصویر لحظه‌ای موجودی'}
            </V2Button>
          </>
        }
      >
        <form
          id="count-session-create-form"
          className="inv-form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void handleCreateSession();
          }}
        >
          {createError ? <V2Alert tone="danger">{createError}</V2Alert> : null}

          <V2FormField
            label="انبار هدف"
            required
            width="full"
            hint={
              warehouses.status === 'ready' &&
              isTruncated(warehouses.data.items.length, warehouses.data.total)
                ? `فقط ${PICKER_LIMIT.toLocaleString('fa-IR')} انبار اول فهرست نمایش داده می‌شود.`
                : undefined
            }
          >
            <select
              className="sk-select"
              value={selectedWarehouseId}
              onChange={(e) => setSelectedWarehouseId(e.target.value)}
            >
              <option value="">انتخاب انبار برای شمارش…</option>
              {warehouseOptions.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </select>
          </V2FormField>

          <V2FormField label="تاریخ انبارگردانی" required width="full">
            <JalaliDateInput
              value={sessionDate}
              onChange={(d) => setSessionDate(d as BusinessDate)}
            />
          </V2FormField>

          <V2FormField label="عنوان یا شرح سشن" width="full">
            <input
              type="text"
              className="sk-input"
              placeholder="مثلاً: انبارگردانی دوره‌ای پاییز ۱۴۰۵"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </V2FormField>
        </form>
      </V2Modal>
    </div>
  );
}
